// ============================================================================
// useCopilotConversations — owns the AM Co-pilot conversation-list rail.
//
// Reads copilot_conversations (RLS scopes to the user automatically) and
// exposes refresh / archive / rename actions for the sidebar UI.
// Added 2026-05-21 alongside the persistent-chat backend layer.
//
// Why no `select('*')` typing dance: the hook deliberately stays loose on
// the row shape to avoid coupling to generated database.types until the
// regenerate step lands. The component layer narrows to CopilotConversation.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type { CopilotConversation } from '../../../shared/types';

const CONV_TABLE = 'copilot_conversations';
const LIST_LIMIT = 50;

export interface UseCopilotConversationsReturn {
  conversations: CopilotConversation[];
  isLoading: boolean;
  error: string | null;
  /**
   * 2026-08-07 (CW-G Phase 1, AFB-0066). Archiving was already a soft flag that
   * hid the chat from the default list — but there was no way back to it, which
   * made it feel like a delete. `showArchived` is the filter that makes archive
   * safely reversible: flip it and archived chats are listed again, with an
   * Unarchive action. Nothing is ever hard-deleted.
   */
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
  refresh: () => Promise<void>;
  archive: (id: string) => Promise<void>;
  unarchive: (id: string) => Promise<void>;
  rename: (id: string, title: string) => Promise<void>;
}

export function useCopilotConversations(): UseCopilotConversationsReturn {
  const [conversations, setConversations] = useState<CopilotConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    const { data, error: qErr } = await supabase
      .from(CONV_TABLE)
      .select('*')
      .eq('is_archived', showArchived)
      .order('updated_at', { ascending: false })
      .limit(LIST_LIMIT);
    if (qErr) {
      setError(qErr.message);
      setConversations([]);
    } else {
      setConversations((data ?? []) as CopilotConversation[]);
    }
    setIsLoading(false);
  }, [showArchived]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Both directions flip the same soft flag. The row leaves the CURRENT list
  // either way (archive drops it from the active list, unarchive drops it from
  // the archived list) — so one optimistic helper covers both.
  const setArchivedFlag = useCallback(
    async (id: string, value: boolean) => {
      const prev = conversations;
      setConversations((c) => c.filter((row) => row.id !== id));
      const { error: uErr } = await supabase
        .from(CONV_TABLE)
        .update({ is_archived: value })
        .eq('id', id);
      if (uErr) {
        setError(uErr.message);
        setConversations(prev);
      }
    },
    [conversations],
  );

  const archive = useCallback(
    (id: string) => setArchivedFlag(id, true),
    [setArchivedFlag],
  );

  const unarchive = useCallback(
    (id: string) => setArchivedFlag(id, false),
    [setArchivedFlag],
  );

  const rename = useCallback(async (id: string, title: string) => {
    const trimmed = title.trim();
    if (trimmed.length === 0) return;
    const { error: uErr } = await supabase
      .from(CONV_TABLE)
      .update({ title: trimmed })
      .eq('id', id);
    if (uErr) {
      setError(uErr.message);
      return;
    }
    setConversations((c) =>
      c.map((row) => (row.id === id ? { ...row, title: trimmed } : row)),
    );
  }, []);

  return {
    conversations,
    isLoading,
    error,
    showArchived,
    setShowArchived,
    refresh,
    archive,
    unarchive,
    rename,
  };
}
